import { useEffect, useRef } from "react";
import * as mqtt from "precompiled-mqtt";
import { IMQTTBrokerSettings } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IMachineCenter } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IClusterMachineCenter } from "contexts/ClusterCachedDataContext/ClusterCachedDataContextInterfaces";
import { MQTTEventsEnum } from "constants/constants";
import { HeartbeatMode, IUseHMIHeartbeatSignalProps } from "./interfaces";
import { buildTopic, publishClusterHeartbeat, publishSingleHeartbeat } from "./helpers";

const { connect } = mqtt;

const HEARTBEAT_INTERVAL_MS = 60000;
const PUBLISH_TIMEOUT_MS = 10000;

const useHMIHeartbeatSignal = ({
    mode,
    machineCenter,
    machineCenters,
    clusterId,
    clusterName,
    mqttBrokerSettings,
    enabled,
}: IUseHMIHeartbeatSignalProps) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const modeRef = useRef<HeartbeatMode>(mode);
    const machineCenterRef = useRef<IMachineCenter | undefined>(machineCenter);
    const machineCentersRef = useRef<IClusterMachineCenter[] | undefined>(machineCenters);
    const clusterIdRef = useRef<number | undefined>(clusterId);
    const clusterNameRef = useRef<string | undefined>(clusterName);
    const mqttBrokerSettingsRef = useRef<IMQTTBrokerSettings | undefined>(mqttBrokerSettings);

    useEffect(() => {
        modeRef.current = mode;
        machineCenterRef.current = machineCenter;
        machineCentersRef.current = machineCenters;
        clusterIdRef.current = clusterId;
        clusterNameRef.current = clusterName;
        mqttBrokerSettingsRef.current = mqttBrokerSettings;
    }, [mode, machineCenter, machineCenters, clusterId, clusterName, mqttBrokerSettings]);

    const publishHeartbeat = async () => {
        const currentMode = modeRef.current;
        const currentMachineCenter = machineCenterRef.current;
        const currentMachineCenters = machineCentersRef.current;
        const currentClusterId = clusterIdRef.current;
        const currentClusterName = clusterNameRef.current;
        const currentMqttBrokerSettings = mqttBrokerSettingsRef.current;

        if (!currentMqttBrokerSettings) {
            return;
        }

        if (currentMode === HeartbeatMode.SINGLE && !currentMachineCenter) {
            return;
        }
        if (
            currentMode === HeartbeatMode.CLUSTER &&
            (!currentMachineCenters ||
                currentMachineCenters.length === 0 ||
                currentClusterId === undefined ||
                !currentClusterName)
        ) {
            return;
        }

        const clientId =
            currentMode === HeartbeatMode.SINGLE
                ? `heartbeat-${currentMachineCenter?.machine_center_id || "unknown"}`
                : `heartbeat-cluster-${currentClusterId || "unknown"}`;

        const topic =
            currentMode === HeartbeatMode.SINGLE
                ? buildTopic(HeartbeatMode.SINGLE, currentMachineCenter!.machine_center_id)
                : buildTopic(HeartbeatMode.CLUSTER, currentClusterId);

        return new Promise<void>((resolve, reject) => {
            try {
                const mosquittoUrl = currentMqttBrokerSettings.host;
                const isWSS = mosquittoUrl.startsWith("wss");
                let connectionHandled = false;
                let client: mqtt.MqttClient | null = null;

                const timeoutId = setTimeout(() => {
                    if (!connectionHandled) {
                        connectionHandled = true;
                        if (client) {
                            client.end();
                        }
                        reject(new Error("Publish timeout"));
                    }
                }, PUBLISH_TIMEOUT_MS);

                client = connect(mosquittoUrl, {
                    username: currentMqttBrokerSettings.user,
                    password: currentMqttBrokerSettings.password,
                    protocol: isWSS ? "wss" : "ws",
                    clean: true,
                    clientId,
                    reconnectPeriod: 0,
                });

                const handleError = (err: Error) => {
                    if (!connectionHandled) {
                        connectionHandled = true;
                        clearTimeout(timeoutId);
                        if (client) {
                            client.end();
                        }
                        console.error("MQTT heartbeat connection error:", err);
                        reject(err);
                    }
                };

                const handleConnect = async () => {
                    if (connectionHandled || !client) {
                        return;
                    }

                    try {
                        if (currentMode === HeartbeatMode.SINGLE && currentMachineCenter) {
                            await publishSingleHeartbeat(client, topic, currentMachineCenter);
                        } else if (
                            currentMode === HeartbeatMode.CLUSTER &&
                            currentMachineCenters &&
                            currentClusterId !== undefined &&
                            currentClusterName
                        ) {
                            await publishClusterHeartbeat(client, topic, currentClusterName, currentMachineCenters);
                        }

                        connectionHandled = true;
                        clearTimeout(timeoutId);
                        client.end();
                        resolve();
                    } catch (error) {
                        connectionHandled = true;
                        clearTimeout(timeoutId);
                        if (client) {
                            client.end();
                        }
                        reject(error);
                    }
                };

                const handleClose = () => {
                    if (!connectionHandled) {
                        connectionHandled = true;
                        clearTimeout(timeoutId);
                        reject(new Error("Connection closed before publish"));
                    }
                };

                client.on(MQTTEventsEnum.ERROR, handleError);
                client.on(MQTTEventsEnum.CONNECT, handleConnect);
                client.on(MQTTEventsEnum.CLOSE, handleClose);
            } catch (error) {
                reject(error);
            }
        });
    };

    useEffect(() => {
        if (!enabled || !mqttBrokerSettings) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const currentMode = modeRef.current;
        const currentMachineCenter = machineCenterRef.current;
        const currentMachineCenters = machineCentersRef.current;
        const currentClusterId = clusterIdRef.current;
        const currentClusterName = clusterNameRef.current;

        if (currentMode === HeartbeatMode.SINGLE && !currentMachineCenter) {
            return;
        }
        if (
            currentMode === HeartbeatMode.CLUSTER &&
            (!currentMachineCenters ||
                currentMachineCenters.length === 0 ||
                currentClusterId === undefined ||
                !currentClusterName)
        ) {
            return;
        }

        publishHeartbeat().catch((error: Error) => {
            console.error("Failed to publish heartbeat:", error);
        });

        intervalRef.current = setInterval(() => {
            publishHeartbeat().catch((error: Error) => {
                console.error("Failed to publish heartbeat:", error);
            });
        }, HEARTBEAT_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, mqttBrokerSettings]);
};

export default useHMIHeartbeatSignal;
